import { useState, useEffect } from 'react';
import {
  Card,
  TextField,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Checkbox,
  Spinner,
} from '@shopify/polaris';
import { fetchJsonWithAuth } from '../utils/fetchWithAuth';

interface AISettingsProps {
  shop: string;
}

interface AISettingsData {
  ai_enabled: boolean;
  ai_section_title: string;
  ai_section_subtitle: string;
  ai_bg_color: string;
  ai_text_color: string;
  ai_system_prompt: string;
  ai_welcome_heading: string;
  ai_welcome_message: string;
  ai_suggested_questions: string[];
}

export default function AISettings({ shop }: AISettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AISettingsData>({
    ai_enabled: true,
    ai_section_title: 'AI Assistant',
    ai_section_subtitle: 'Chat with our AI for instant help',
    ai_bg_color: '#008060',
    ai_text_color: '#ffffff',
    ai_system_prompt: 'You are a helpful customer service assistant. Answer questions about orders, shipping, and returns based on the FAQ knowledge base provided.',
    ai_welcome_heading: 'Hi! I\'m your AI assistant',
    ai_welcome_message: 'I can help you with questions about orders, shipping, returns, and more.',
    ai_suggested_questions: ['How long does shipping take?', 'What is your return policy?', 'Do you ship internationally?'],
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, [shop]);

  const loadSettings = async () => {
    try {
      const data = await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`);
      
      let suggestedQuestions = ['How long does shipping take?', 'What is your return policy?', 'Do you ship internationally?'];
      if (data.ai_suggested_questions) {
        try {
          suggestedQuestions = typeof data.ai_suggested_questions === 'string' 
            ? JSON.parse(data.ai_suggested_questions)
            : data.ai_suggested_questions;
        } catch (e) {
          console.error('Failed to parse suggested questions:', e);
        }
      }
      
      setSettings({
        ai_enabled: data.ai_enabled === 1 || data.ai_enabled === true,
        ai_section_title: data.ai_section_title || 'AI Assistant',
        ai_section_subtitle: data.ai_section_subtitle || 'Chat with our AI for instant help',
        ai_bg_color: data.ai_bg_color || '#008060',
        ai_text_color: data.ai_text_color || '#ffffff',
        ai_system_prompt: data.ai_system_prompt || 'You are a helpful customer service assistant. Answer questions about orders, shipping, and returns based on the FAQ knowledge base provided.',
        ai_welcome_heading: data.ai_welcome_heading || 'Hi! I\'m your AI assistant',
        ai_welcome_message: data.ai_welcome_message || 'I can help you with questions about orders, shipping, returns, and more.',
        ai_suggested_questions: suggestedQuestions,
      });
    } catch (err) {
      setError('Failed to load AI settings. Session may have expired - redirecting...');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`, {
        method: 'PUT',
        body: JSON.stringify({
          ai_enabled: settings.ai_enabled ? 1 : 0,
          ai_section_title: settings.ai_section_title,
          ai_section_subtitle: settings.ai_section_subtitle,
          ai_bg_color: settings.ai_bg_color,
          ai_text_color: settings.ai_text_color,
          ai_system_prompt: settings.ai_system_prompt,
          ai_welcome_heading: settings.ai_welcome_heading,
          ai_welcome_message: settings.ai_welcome_message,
          ai_suggested_questions: JSON.stringify(settings.ai_suggested_questions),
        }),
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save AI settings. Session may have expired - redirecting...');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToDefault = () => {
    if (!confirm('Are you sure you want to revert all AI Assistant settings to their default values? This cannot be undone.')) {
      return;
    }

    setSettings({
      ai_enabled: true,
      ai_section_title: 'AI Assistant',
      ai_section_subtitle: 'Chat with our AI for instant help',
      ai_bg_color: '#008060',
      ai_text_color: '#ffffff',
      ai_system_prompt: 'You are a helpful customer service assistant. Answer questions about orders, shipping, and returns based on the FAQ knowledge base provided.',
      ai_welcome_heading: 'Hi! I\'m your AI assistant',
      ai_welcome_message: 'I can help you with questions about orders, shipping, returns, and more.',
      ai_suggested_questions: ['How long does shipping take?', 'What is your return policy?', 'Do you ship internationally?'],
    });

    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  if (loading) {
    return (
      <Card>
        <BlockStack align="center">
          <Spinner size="small" />
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="500">
      {success && (
        <Banner status="success" onDismiss={() => setSuccess(false)}>
          AI settings saved successfully
        </Banner>
      )}

      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {/* Save and Revert Buttons at the top */}
      <div style={{ 
        marginBottom: '32px',
        padding: '28px',
        backgroundColor: '#f6f6f7',
        borderRadius: '10px',
        border: '3px solid #008060',
        boxShadow: '0 4px 16px rgba(0, 128, 96, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px'
      }}>
        <Button 
          primary 
          onClick={handleSave} 
          loading={saving} 
          size="large"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            backgroundColor: '#008060',
            borderColor: '#008060',
            boxShadow: '0 6px 16px rgba(0, 128, 96, 0.4)',
            transform: 'scale(1.03)',
            transition: 'all 0.2s ease',
            letterSpacing: '0.5px'
          }}
        >
          💾 Save AI Settings
        </Button>
        <Button 
          onClick={handleRevertToDefault} 
          loading={saving}
          size="large"
          tone="critical"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            letterSpacing: '0.5px'
          }}
        >
          🔄 Revert to Default
        </Button>
      </div>

      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">
            AI Assistant Section Settings
          </Text>

          <Checkbox
            label="Enable AI Assistant Section"
            checked={settings.ai_enabled}
            onChange={(checked) => setSettings({ ...settings, ai_enabled: checked })}
            helpText="Show or hide the AI chat widget on the customer page"
          />

          <TextField
            label="Section Title"
            value={settings.ai_section_title}
            onChange={(value) => setSettings({ ...settings, ai_section_title: value })}
            helpText="Title shown in the chat header"
          />

          <TextField
            label="Section Subtitle"
            value={settings.ai_section_subtitle}
            onChange={(value) => setSettings({ ...settings, ai_section_subtitle: value })}
            helpText="Subtitle shown in the chat header"
          />

          <InlineStack gap="400">
            <div style={{ flex: 1 }}>
              <TextField
                label="Header Background Color"
                value={settings.ai_bg_color}
                onChange={(value) => setSettings({ ...settings, ai_bg_color: value })}
                type="color"
                helpText="Chat header background"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Header Text Color"
                value={settings.ai_text_color}
                onChange={(value) => setSettings({ ...settings, ai_text_color: value })}
                type="color"
                helpText="Header text color"
              />
            </div>
          </InlineStack>

          <TextField
            label="Welcome Heading"
            value={settings.ai_welcome_heading}
            onChange={(value) => setSettings({ ...settings, ai_welcome_heading: value })}
            helpText="Main greeting shown when chat is empty"
          />

          <TextField
            label="Welcome Message"
            value={settings.ai_welcome_message}
            onChange={(value) => setSettings({ ...settings, ai_welcome_message: value })}
            multiline={3}
            helpText="Description shown below the welcome heading"
          />

          <BlockStack gap="300">
            <Text variant="bodyMd" as="p" fontWeight="semibold">
              Suggested Questions
            </Text>
            <Text variant="bodySm" as="p" color="subdued">
              Quick-start questions customers can click (one per line, max 3)
            </Text>
            {settings.ai_suggested_questions.map((question, index) => (
              <InlineStack key={index} gap="200" align="center">
                <div style={{ flex: 1 }}>
                  <TextField
                    label=""
                    value={question}
                    onChange={(value) => {
                      const newQuestions = [...settings.ai_suggested_questions];
                      newQuestions[index] = value;
                      setSettings({ ...settings, ai_suggested_questions: newQuestions });
                    }}
                    placeholder={`Question ${index + 1}`}
                    autoComplete="off"
                  />
                </div>
                {settings.ai_suggested_questions.length > 1 && (
                  <Button
                    onClick={() => {
                      const newQuestions = settings.ai_suggested_questions.filter((_, i) => i !== index);
                      setSettings({ ...settings, ai_suggested_questions: newQuestions });
                    }}
                    tone="critical"
                  >
                    Remove
                  </Button>
                )}
              </InlineStack>
            ))}
            {settings.ai_suggested_questions.length < 5 && (
              <Button
                onClick={() => {
                  setSettings({
                    ...settings,
                    ai_suggested_questions: [...settings.ai_suggested_questions, '']
                  });
                }}
              >
                Add Question
              </Button>
            )}
          </BlockStack>

          <TextField
            label="System Prompt"
            value={settings.ai_system_prompt}
            onChange={(value) => setSettings({ ...settings, ai_system_prompt: value })}
            multiline={8}
            helpText="Define how the AI assistant behaves. The AI will automatically learn from all your FAQs."
            autoComplete="off"
          />

          <Banner status="info">
            <BlockStack gap="200">
              <Text as="p" fontWeight="semibold">How AI Chat Works:</Text>
              <Text as="p">
                • The AI assistant automatically learns from all your FAQs
              </Text>
              <Text as="p">
                • It will answer customer questions based on your FAQ knowledge base
              </Text>
              <Text as="p">
                • Customize the system prompt to adjust the AI's personality and behavior
              </Text>
              <Text as="p">
                • The AI chat appears as a floating button on the customer page
              </Text>
            </BlockStack>
          </Banner>

          <Banner status="warning">
            <Text as="p">
              <strong>Important:</strong> Make sure OPENAI_API_KEY is set in your environment variables for AI chat to work.
            </Text>
          </Banner>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
